import React, { useState, useEffect } from "react";
import { api, getToken } from "../../../software/api";
import { toast } from "sonner";
import { X, Trash2, Upload, FileText, ShieldCheck, Download, Plus } from "lucide-react";
import { API, cellInp, HdrBtn, money, fmtDate, ProductSearch } from "./shared";
import { printFM84 } from "./printDocs";
import { imgUrl } from "../showroom/catalogShared";
import { EmailRecipientsModal } from "./EmailRecipientsModal";

/* ============ shared document editor (Quotes + Invoices) ============ */
export function DocEditor({ title, doc, accent = "#123a63", hideStatus, statuses, brands, defaultBrand, contacts, onSave, onClose }) {
  const [items, setItems] = useState((doc?.items || []).map((i) => ({ product_id: i.product_id, name: i.name, qty: i.qty, unit_price: i.unit_price, discount: i.discount || 0, colour_name: i.colour_name || "", colour_image: i.colour_image || "", colours: i.colours || null })));
  const [spaIds, setSpaIds] = useState(new Set());
  React.useEffect(() => { api("GET", "/spas/catalogue").then((r) => setSpaIds(new Set(r.map((x) => x.id)))).catch(() => {}); }, []);
  const [discount, setDiscount] = useState(doc?.discount || 0);
  const [notes, setNotes] = useState(doc?.notes || "");
  const [status, setStatus] = useState(doc?.status || "draft");
  const [brand, setBrand] = useState(doc?.brand || defaultBrand || (brands && brands[0]) || "");
  const contactList = contacts || [];
  const isQuote = /quote/i.test(title || "");
  const defKey = isQuote ? "default_quotes" : "default_accounts";
  const nameOf = (c) => `${c.first_name || ""} ${c.surname || ""}`.trim();
  const initAddr = () => {
    if (doc?.addressed_to) {
      const wanted = String(doc.addressed_to).split(";").map((s) => s.trim()).filter(Boolean);
      const idxs = wanted.map((w) => contactList.findIndex((c) => nameOf(c) === w)).filter((i) => i >= 0).map(String);
      if (idxs.length) return idxs;
    }
    let i = !isQuote ? contactList.findIndex((c) => c.bill_to) : -1;
    if (i < 0) i = contactList.findIndex((c) => c[defKey]);
    if (i < 0) i = contactList.findIndex((c) => c.default_correspondence);
    if (i < 0) i = contactList.findIndex((c) => c.billing);
    return i >= 0 ? [String(i)] : [];
  };
  const [addrIdxs, setAddrIdxs] = useState(initAddr);
  const toggleAddr = (i) => setAddrIdxs((a) => a.includes(String(i)) ? a.filter((x) => x !== String(i)) : [...a, String(i)]);
  const [pick, setPick] = useState(false);
  const [labourRates, setLabourRates] = useState([]);
  useEffect(() => { api("GET", "/labour-charges").then((r) => setLabourRates(r || [])).catch(() => {}); }, []);
  const [colourPick, setColourPick] = useState(null);
  const [saving, setSaving] = useState(false);
  const STAT = statuses || [["draft", "Draft"], ["sent", "Sent"], ["accepted", "Accepted"], ["declined", "Declined"]];
  const setItem = (i, patch) => setItems(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const subtotal = items.reduce((s, i) => s + (i.qty * i.unit_price - (i.discount || 0)), 0);
  const total = Math.max(subtotal - (discount || 0), 0);
  const save = async () => {
    if (items.length === 0) { toast.error("Add at least one item."); return; }
    setSaving(true);
    const chosen = addrIdxs.map(Number).map((i) => contactList[i]).filter(Boolean);
    const names = chosen.map(nameOf).filter(Boolean);
    const emails = chosen.map((c) => c.email || "").filter(Boolean);
    const addressing = {
      addressed_to: names.join("; "),
      addressed_relationship: chosen[0] ? (chosen[0].relationship || "") : "",
      addressed_email: emails.join("; "),
      addressed_company: chosen[0] ? (chosen[0].company || "") : "",
      addressed_address: chosen[0] ? (chosen[0].address_text || "") : "",
    };
    try { await onSave({ items: items.map((i) => ({ ...i, qty: Math.round(i.qty) || 1 })), discount: Number(discount) || 0, notes, status, brand, ...addressing }); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="doc-editor">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-2xl mt-8">
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ background: accent, color: "white" }}>
          <h3 className="font-black text-sm" data-testid="doc-editor-title">{title}</h3>
          <button onClick={onClose} data-testid="doc-editor-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-slate-600"><tr><th className="px-2 py-1 text-left">Item</th><th className="w-16 text-center">Qty</th><th className="w-24 text-right">Unit</th><th className="w-24 text-right">Disc</th><th className="w-24 text-right">Nett</th><th className="w-8" /></tr></thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={6} className="px-2 py-3 text-center text-slate-400">No items — add a product or a manual line.</td></tr>}
                {items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid="doc-editor-line">
                    <td className="px-1 py-0.5">
                      <input value={it.name} onChange={(e) => setItem(i, { name: e.target.value })} className={cellInp} data-testid="doc-line-name" />
                      {spaIds.has(it.product_id) ? (
                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5" data-testid="doc-line-spa-colours">
                          {SPA_COLOUR_ITEMS.map(([item, label]) => {
                            const c = (it.colours || []).find((x) => x.item === item);
                            return (
                              <span key={item} className="flex items-center gap-0.5">
                                {c?.image ? <img src={imgUrl(c.image)} alt="" className="h-4 w-4 rounded-sm object-cover border border-slate-300" /> : null}
                                <button type="button" onClick={() => setColourPick({ i, item, model: it.product_id })} data-testid={`doc-line-colour-${item.toLowerCase()}`} className="text-[10px] font-bold text-[#4a5a1e] hover:underline">
                                  {c ? `${label}: ${c.name}` : `+ ${label}`}
                                </button>
                                {c && <button type="button" onClick={() => setItem(i, { colours: (it.colours || []).filter((x) => x.item !== item) })} className="text-[10px] text-slate-300 hover:text-rose-600">✕</button>}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                      <div className="flex items-center gap-1 mt-0.5">
                        {it.colour_image ? <img src={it.colour_image} alt="" className="h-4 w-4 rounded-sm object-cover border border-slate-300" /> : null}
                        <button type="button" onClick={() => setColourPick(i)} data-testid="doc-line-colour" className="text-[10px] font-bold text-[#4a5a1e] hover:underline">
                          {it.colour_name ? `Colour: ${it.colour_name}` : "+ Colour"}
                        </button>
                        {it.colour_name && <button type="button" onClick={() => setItem(i, { colour_name: "", colour_image: "" })} className="text-[10px] text-slate-300 hover:text-rose-600">✕</button>}
                      </div>
                      )}
                    </td>
                    <td className="px-1 py-0.5"><input type="number" value={it.qty} onChange={(e) => setItem(i, { qty: parseFloat(e.target.value) || 0 })} className={`${cellInp} text-center`} data-testid="doc-line-qty" /></td>
                    <td className="px-1 py-0.5"><input type="number" step="0.01" value={it.unit_price} onChange={(e) => setItem(i, { unit_price: parseFloat(e.target.value) || 0 })} className={`${cellInp} text-right`} data-testid="doc-line-unit" /></td>
                    <td className="px-1 py-0.5"><input type="number" step="0.01" value={it.discount} onChange={(e) => setItem(i, { discount: parseFloat(e.target.value) || 0 })} className={`${cellInp} text-right`} /></td>
                    <td className="px-2 py-0.5 text-right font-mono2">{money(it.qty * it.unit_price - (it.discount || 0))}</td>
                    <td className="text-center"><button onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5 text-slate-300 hover:text-rose-600" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <HdrBtn testid="doc-add-product" onClick={() => setPick(true)}>+ Add Product</HdrBtn>
            <HdrBtn testid="doc-add-manual" onClick={() => setItems([...items, { product_id: null, name: "", qty: 1, unit_price: 0, discount: 0 }])}>+ Add Manual Line</HdrBtn>
            <select data-testid="doc-add-labour" defaultValue="" value=""
              onChange={(e) => { const n = e.target.value; if (!n) return; const rc = labourRates.find((r) => r.name === n); setItems([...items, { product_id: null, name: n, qty: 1, unit_price: rc ? rc.rate : 0, discount: 0, colour_name: "", colour_image: "" }]); }}
              className="text-[12px] rounded border border-[#123a63] text-[#123a63] font-bold px-2 py-[5px] bg-white hover:bg-[#eef3f9] cursor-pointer">
              <option value="">+ Labour preset ▾</option>
              {labourRates.map((p) => <option key={p.name} value={p.name}>{p.name} — ${p.rate.toFixed(2)}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-[11px] font-bold text-slate-500">Notes</label>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={cellInp} data-testid="doc-notes" />
            </div>
            {brands && brands.length > 0 && (
              <div>
                <label className="block text-[11px] font-bold text-slate-500">Brand (Quote By)</label>
                <select value={brand} onChange={(e) => setBrand(e.target.value)} className={cellInp} data-testid="doc-brand">
                  {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            )}
            {!hideStatus && (
              <div>
                <label className="block text-[11px] font-bold text-slate-500">Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className={cellInp} data-testid="doc-status">
                  {STAT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            )}
            {contactList.length > 0 && (
              <div className="min-w-[190px]">
                <label className="block text-[11px] font-bold text-slate-500">Address to {isQuote ? "(Quotes default ✓)" : "(Accounts default ✓)"} — tick one or more</label>
                <div className="max-h-[92px] overflow-y-auto border border-slate-200 rounded p-1 space-y-0.5" data-testid="doc-addressed-to">
                  {contactList.map((c, i) => (
                    <label key={i} className="flex items-center gap-1.5 text-[12px] cursor-pointer hover:bg-slate-50 rounded px-0.5">
                      <input type="checkbox" checked={addrIdxs.includes(String(i))} onChange={() => toggleAddr(i)} data-testid={`doc-addr-opt-${i}`} className="h-3.5 w-3.5 accent-[#123a63]" />
                      <span className="truncate">{nameOf(c) || "Contact"}{c.default_quotes ? " ·Q" : ""}{c.default_accounts ? " ·A" : ""}{c.bill_to ? " ·BILL" : ""}{c.relationship ? ` — ${c.relationship}` : ""}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end text-[12px]"><span className="text-slate-500">Order discount</span>
                <input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)} className={`${cellInp} w-24 text-right`} data-testid="doc-discount" /></div>
              <div className="mt-1 text-sm font-black text-slate-800" data-testid="doc-total">Total: {money(total)}</div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 rounded hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="doc-save" className="px-5 py-2 text-sm font-black text-white rounded" style={{ background: accent }}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
      {pick && <ProductSearch onClose={() => setPick(false)} onPick={(p) => { setItems([...items, { product_id: p.id, name: p.name, qty: 1, unit_price: p.price, discount: 0, colour_name: "", colour_image: "" }]); setPick(false); }} />}
      {colourPick !== null && <ColourPicker item={colourPick?.item} model={colourPick?.model} onClose={() => setColourPick(null)} onPick={(c) => {
        if (typeof colourPick === "number") setItem(colourPick, { colour_name: c.name, colour_image: c.image_url || "" });
        else { const cur = (items[colourPick.i].colours || []).filter((x) => x.item !== colourPick.item); setItem(colourPick.i, { colours: [...cur, { item: colourPick.item, name: c.name, image: c.image_url || "" }] }); }
        setColourPick(null); }} />}
    </div>
  );
}

/* ============ Colour swatch picker (attach a shell/cabinet colour to a line) ============ */
const SPA_COLOUR_ITEMS = [["Shell", "Shell"], ["Cabinet", "Cabinet"], ["Cover", "Hard Cover"]];

function ColourPicker({ onPick, onClose, item, model }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  React.useEffect(() => { api("GET", "/colours").then(setRows).catch(() => {}); }, []);
  const shown = rows.filter((c) => (!item || c.item === item) && (!model || !c.models || c.models.includes(model))).filter((c) => !q || (c.name || "").toLowerCase().includes(q.toLowerCase()) || (c.item || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center p-6" data-testid="colour-picker">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-2xl mt-12 overflow-hidden">
        <div className="flex items-center gap-2 p-3 border-b bg-[#eef3d9]">
          <span className="font-black text-sm text-[#4a5a1e]">Choose a {item === "Cover" ? "Hard Cover" : item || ""} Colour</span>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search colours…" className="flex-1 text-sm outline-none bg-white border border-[#b7c489] rounded px-2 py-1 ml-2" data-testid="colour-picker-search" />
          <button onClick={onClose} data-testid="colour-picker-close"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {shown.length === 0 && <p className="col-span-full p-4 text-sm text-slate-400 text-center">No colours. Add them under Colour Swatches.</p>}
          {shown.map((c) => (
            <button key={c.id} onClick={() => onPick(c)} data-testid="colour-picker-option" className="rounded-lg border-2 border-slate-200 overflow-hidden hover:border-[#7ba03a] text-left">
              <div className="aspect-square bg-slate-50 flex items-center justify-center">
                {c.image_url ? <img src={imgUrl(c.image_url)} alt={c.name} className="h-full w-full object-cover" /> : <span className="text-[10px] text-slate-300">no photo</span>}
              </div>
              <div className="px-1.5 py-1"><p className="text-[11px] font-semibold text-slate-700 truncate">{c.name}</p></div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============ generic file gallery (client files + company docs) ============ */
function FileGallery({ title, files, uploadUrl, onDeleted, onUploaded, onClose, deleteBase,
  accept = "image/*,application/pdf", uploadLabel = "Upload (images, PDF)" }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const isImg = (ct) => (ct || "").startsWith("image/");
  const base = process.env.REACT_APP_BACKEND_URL;
  const uploadFiles = async (list) => {
    list = Array.from(list || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData(); fd.append("file", f);
        const res = await fetch(`${API}${uploadUrl}`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
        if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "Upload failed"); }
      }
      toast.success("File(s) uploaded"); onUploaded();
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };
  const onUpload = async (e) => { await uploadFiles(e.target.files); e.target.value = ""; };
  const onDrop = async (e) => { e.preventDefault(); setDragOver(false); await uploadFiles(e.dataTransfer.files); };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="files-modal">
      <div className="w-full max-w-3xl bg-white rounded-lg shadow-2xl mt-8">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-[#123a63] text-white">
          <h3 className="font-black text-sm">{title}</h3>
          <button onClick={onClose} data-testid="files-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4">
          <label
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            data-testid="files-dropzone"
            className={`flex flex-col items-center justify-center gap-1 w-full py-6 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${dragOver ? "border-[#123a63] bg-sky-50" : "border-slate-300 bg-slate-50 hover:bg-slate-100"}`}>
            <Upload className={`h-6 w-6 ${dragOver ? "text-[#123a63]" : "text-slate-400"}`} />
            <span className="text-sm font-bold text-[#123a63]" data-testid="files-upload-label">{uploading ? "Uploading…" : (dragOver ? "Drop to upload" : "Drag & drop files here, or click to browse")}</span>
            <span className="text-[11px] text-slate-400">{uploadLabel} · max 25MB each</span>
            <input type="file" multiple accept={accept} onChange={onUpload} className="hidden" data-testid="files-upload-input" disabled={uploading} />
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            {files.length === 0 && <p className="col-span-full text-slate-400 text-sm">No files yet.</p>}
            {files.map((f) => (
              <div key={f.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid="file-card">
                <a href={`${base}${f.url}`} target="_blank" rel="noreferrer" className="block h-28 bg-slate-50 flex items-center justify-center">
                  {isImg(f.content_type) ? <img src={`${base}${f.url}`} alt={f.name} className="h-full w-full object-cover" /> : <FileText className="h-10 w-10 text-slate-400" />}
                </a>
                <div className="p-2">
                  <p className="text-[11px] text-slate-700 truncate" title={f.name}>{f.name}</p>
                  <div className="flex justify-between items-center mt-1">
                    <a href={`${base}${f.url}`} target="_blank" rel="noreferrer" className="text-[11px] text-sky-700 inline-flex items-center gap-1"><Download className="h-3 w-3" />Open</a>
                    <button onClick={() => { if (window.confirm("Delete this file?")) api("DELETE", `${deleteBase}/${f.id}`).then(onDeleted).catch((e) => toast.error(e.message)); }} data-testid="file-delete"><Trash2 className="h-3.5 w-3.5 text-slate-300 hover:text-rose-600" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const DOC_ACCEPT = "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.eml,.msg";
export function FilesModal({ cid, files, reload, onClose, clientName }) {
  return <FileGallery title={`Client's File Bank — ${clientName}`} files={files} uploadUrl={`/clients/${cid}/files`}
    deleteBase="/client-files" accept={DOC_ACCEPT} uploadLabel="Images, PDF, docs, spreadsheets, .eml/.msg"
    onDeleted={reload} onUploaded={reload} onClose={onClose} />;
}
export function CompanyDocsModal({ docs, reload, onClose }) {
  return <FileGallery title="Company Documents" files={docs} uploadUrl="/company-docs"
    deleteBase="/company-docs" accept={DOC_ACCEPT} uploadLabel="Images, PDF, docs, spreadsheets, .eml/.msg"
    onDeleted={reload} onUploaded={reload} onClose={onClose} />;
}
export function SupplierFilesModal({ sid, files, reload, onClose, supplierName }) {
  return <FileGallery title={`Supplier Files — ${supplierName}`} files={files} uploadUrl={`/suppliers/${sid}/files`}
    deleteBase="/supplier-files" accept={DOC_ACCEPT} uploadLabel="Images, PDF, docs, spreadsheets, .eml/.msg"
    onDeleted={reload} onUploaded={reload} onClose={onClose} />;
}

/* ============ FM84 — Spa Supplier Warranty Submission ============ */
export function FM84Modal({ cid, client, fm84s, team, suppliers, wo, brand, reload, onClose }) {
  const [f, setF] = useState({
    warranty_authority: wo?.warranty_authority || "", spa_make: "", spa_model: client?.pool?.type || "",
    serial: "", purchase_date: "", fault_description: wo?.issue_text || "", technician: wo?.technician || "",
    resolution: wo?.result_text || "", hccp_valid: "", hccp_no: "", pop_attached: "",
    supplier: "Spa Industries Global", status: "submitted",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async () => {
    if (!f.fault_description.trim()) { toast.error("Describe the fault."); return; }
    setSaving(true);
    try {
      await api("POST", "/fm84", { client_id: cid, wo_id: wo?.id || null, ...f, purchase_date: f.purchase_date || null });
      toast.success("FM84 warranty submission created"); reload();
      setF({ ...f, serial: "", fault_description: "" });
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const printFm = (d) => printFM84(d, client, wo, brand);
  const supplierNames = (suppliers || []).map((s) => s.name);
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="fm84-modal">
      <div className="w-full max-w-3xl bg-white rounded-lg shadow-2xl mt-6">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-[#7a4a00] text-white">
          <h3 className="font-black text-sm inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4" />Warranty Claims</h3>
          <button onClick={onClose} data-testid="fm84-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-2">
            <div><label className="block text-[11px] font-bold text-slate-500">Warranty Authority</label><input value={f.warranty_authority} onChange={upd("warranty_authority")} className={cellInp} data-testid="fm84-warranty_authority" /></div>
            <div><label className="block text-[11px] font-bold text-slate-500">Supplier</label>
              <input list="fm84-suppliers" value={f.supplier} onChange={upd("supplier")} className={cellInp} data-testid="fm84-supplier" />
              <datalist id="fm84-suppliers">{supplierNames.map((n) => <option key={n} value={n} />)}</datalist></div>
            <div><label className="block text-[11px] font-bold text-slate-500">Spa Make</label><input value={f.spa_make} onChange={upd("spa_make")} className={cellInp} data-testid="fm84-spa_make" /></div>
            <div><label className="block text-[11px] font-bold text-slate-500">Spa Model</label><input value={f.spa_model} onChange={upd("spa_model")} className={cellInp} data-testid="fm84-spa_model" /></div>
            <div><label className="block text-[11px] font-bold text-slate-500">Serial No.</label><input value={f.serial} onChange={upd("serial")} className={cellInp} data-testid="fm84-serial" /></div>
            <div><label className="block text-[11px] font-bold text-slate-500">Purchase Date</label><input type="date" value={f.purchase_date} onChange={upd("purchase_date")} className={cellInp} data-testid="fm84-purchase_date" /></div>
            <div><label className="block text-[11px] font-bold text-slate-500">Technician</label>
              <select value={f.technician} onChange={upd("technician")} className={cellInp} data-testid="fm84-technician">
                <option value="">—</option>{(team || []).map((t) => <option key={t}>{t}</option>)}</select></div>
            <div><label className="block text-[11px] font-bold text-slate-500">Status</label>
              <select value={f.status} onChange={upd("status")} className={cellInp} data-testid="fm84-status">
                {["submitted", "approved", "declined", "completed"].map((s) => <option key={s}>{s}</option>)}</select></div>
          </div>
          <div><label className="block text-[11px] font-bold text-slate-500">Fault Description</label>
            <textarea rows={3} value={f.fault_description} onChange={upd("fault_description")} className={cellInp} data-testid="fm84-fault" /></div>
          <div><label className="block text-[11px] font-bold text-slate-500">Details of attempt to resolve (who / what / when / how)</label>
            <textarea rows={2} value={f.resolution} onChange={upd("resolution")} className={cellInp} data-testid="fm84-resolution" /></div>
          <div className="grid sm:grid-cols-3 gap-2">
            <div><label className="block text-[11px] font-bold text-slate-500">HCCP Valid</label><input value={f.hccp_valid} onChange={upd("hccp_valid")} className={cellInp} data-testid="fm84-hccp-valid" placeholder="Yes / No" /></div>
            <div><label className="block text-[11px] font-bold text-slate-500">HCCP No.</label><input value={f.hccp_no} onChange={upd("hccp_no")} className={cellInp} data-testid="fm84-hccp-no" /></div>
            <div><label className="block text-[11px] font-bold text-slate-500">POP Attached</label><input value={f.pop_attached} onChange={upd("pop_attached")} className={cellInp} data-testid="fm84-pop" placeholder="Yes / No" /></div>
          </div>
          <div className="flex justify-end">
            <button onClick={create} disabled={saving} data-testid="fm84-create" className="px-5 py-2 text-sm font-black text-white rounded bg-[#7a4a00] hover:bg-[#8f5900]">{saving ? "Saving…" : "Create FM84"}</button>
          </div>
          <div>
            <h4 className="text-[12px] font-black text-slate-600 mb-1">Existing FM84s for this client</h4>
            <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-52 overflow-y-auto">
              {fm84s.length === 0 && <p className="p-2 text-slate-400 text-sm">None yet.</p>}
              {fm84s.map((d) => (
                <div key={d.id} className="flex items-center justify-between px-3 py-2 text-[12px]" data-testid="fm84-row">
                  <span><b className="font-mono2 text-[#7a4a00]">#{d.fm84_number}</b> · {fmtDate(d.created_at)} · <span className="capitalize">{d.status}</span> · {d.spa_make} {d.spa_model}</span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => printFm(d)} data-testid="fm84-print" className="text-sky-700 font-bold hover:underline">Print</button>
                    <button onClick={() => { if (window.confirm(`Delete FM84 #${d.fm84_number}?`)) api("DELETE", `/fm84/${d.id}`).then(reload); }} data-testid="fm84-delete"><Trash2 className="h-3.5 w-3.5 text-slate-300 hover:text-rose-600" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ Parts order (create, linked to a WO) ============ */
const PO_STATUS = [["to_order", "To Order"], ["ordered", "Ordered"], ["received", "Received"], ["cancelled", "Cancelled"]];

const orderLines = (o) => (o.lines && o.lines.length) ? o.lines : [{ part_name: o.part_name, qty: o.qty, cost: o.cost }];
const orderQty = (o) => orderLines(o).reduce((s, l) => s + (Number(l.qty) || 0), 0);
const orderCost = (o) => orderLines(o).reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.cost) || 0), 0);

export function PartsOrderModal({ cid, wo, suppliers, reload, onClose, client, brand, employee }) {
  const [f, setF] = useState({ supplier: "", status: "to_order", notes: "" });
  const [lines, setLines] = useState([{ part_name: "", qty: 1, cost: 0 }]);
  const [saving, setSaving] = useState(false);
  const [emailFor, setEmailFor] = useState(null);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, { part_name: "", qty: 1, cost: 0 }]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));
  const total = lines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.cost) || 0), 0);
  const save = async (process) => {
    const clean = lines.map((l) => ({ part_name: (l.part_name || "").trim(), qty: parseFloat(l.qty) || 1, cost: parseFloat(l.cost) || 0 })).filter((l) => l.part_name);
    if (!clean.length) { toast.error("Add at least one part."); return; }
    setSaving(true);
    try {
      const po = await api("POST", `/parts-orders${process && client ? "?process=true" : ""}`, { client_id: cid, wo_id: wo?.id || null, wo_number: wo?.wo_number || null,
        supplier: f.supplier, status: f.status, notes: f.notes, lines: clean,
        part_name: clean[0].part_name, qty: clean[0].qty, cost: clean[0].cost });
      toast.success(`Part order GLA${po.order_number} created`);
      if (process && client) { const s = (suppliers || []).find((x) => (x.name || "").toLowerCase() === (f.supplier || "").toLowerCase()) || {}; setEmailFor({ po: { ...po, _contact: s.contact || "" }, s }); return; }
      reload(); onClose();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  if (emailFor) return <EmailRecipientsModal client={client} kind="parts_order" doc={emailFor.po} brand={brand} employee={employee}
    toInit={{ name: emailFor.s.contact || emailFor.s.name || f.supplier, email: emailFor.s.email || "" }} noteType={`Part/s Ordered GLA${emailFor.po.order_number}`}
    onSent={() => api("POST", `/parts-orders/${emailFor.po.id}/emailed`).catch((e) => toast.error(`Order not marked as ordered: ${e.message}`))} onClose={() => { reload(); onClose(); }} />;
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="parts-order-modal">
      <div className="w-full max-w-xl bg-white rounded-lg shadow-2xl mt-12">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-[#c81e1e] text-white">
          <h3 className="font-black text-sm">Order Parts {wo ? `· W/O ${wo.wo_number}` : ""}</h3>
          <button onClick={onClose} data-testid="parts-order-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-[11px] font-bold text-slate-500">Supplier</label>
              <input list="po-suppliers" value={f.supplier} onChange={upd("supplier")} className={cellInp} data-testid="parts-order-supplier" />
              <datalist id="po-suppliers">{(suppliers || []).map((s) => <option key={s.id} value={s.name} />)}</datalist></div>
            <div><label className="block text-[11px] font-bold text-slate-500">Status</label>
              <select value={f.status} onChange={upd("status")} className={cellInp} data-testid="parts-order-status-new">
                {PO_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[11px] font-bold text-slate-500">Parts to order (add as many as you need)</label>
              <button onClick={addLine} data-testid="parts-order-add-line" className="inline-flex items-center gap-1 text-[12px] font-bold text-[#c81e1e] hover:underline"><Plus className="h-3.5 w-3.5" /> Add Part</button>
            </div>
            <div className="border border-slate-200 rounded overflow-hidden">
              <div className="grid grid-cols-[1fr_72px_90px_32px] gap-0 bg-slate-50 text-[10px] font-bold uppercase text-slate-500 px-2 py-1">
                <span>Part</span><span className="text-center">Qty</span><span className="text-right">Unit Cost</span><span />
              </div>
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_72px_90px_32px] gap-1 items-center px-2 py-1 border-t border-slate-100" data-testid={`parts-order-line-${i}`}>
                  <input value={l.part_name} onChange={(e) => setLine(i, "part_name", e.target.value)} placeholder="Part name" className={cellInp} data-testid={`parts-order-part-${i}`} />
                  <input type="number" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} className={`${cellInp} text-center`} data-testid={`parts-order-qty-${i}`} />
                  <input type="number" step="0.01" value={l.cost} onChange={(e) => setLine(i, "cost", e.target.value)} className={`${cellInp} text-right`} data-testid={`parts-order-cost-${i}`} />
                  <button onClick={() => removeLine(i)} disabled={lines.length <= 1} data-testid={`parts-order-remove-${i}`} title="Remove part" className="text-slate-300 hover:text-rose-600 disabled:opacity-30 flex justify-center"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <div className="flex justify-between items-center px-2 py-1.5 border-t border-slate-200 bg-slate-50 text-[12px]">
                <span className="font-bold text-slate-500">{lines.length} line{lines.length !== 1 ? "s" : ""}</span>
                <span className="font-black text-slate-700" data-testid="parts-order-total">Est. total {money(total)}</span>
              </div>
            </div>
          </div>
          <div><label className="block text-[11px] font-bold text-slate-500">Notes</label><input value={f.notes} onChange={upd("notes")} className={cellInp} data-testid="parts-order-notes" /></div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 rounded hover:bg-slate-100">Cancel</button>
          <button onClick={() => save(false)} disabled={saving} data-testid="parts-order-save" className="px-4 py-2 text-sm font-bold text-[#c81e1e] border border-[#c81e1e] rounded hover:bg-red-50">{saving ? "Saving…" : "Create Order"}</button>
          {client && <button onClick={() => save(true)} disabled={saving} data-testid="parts-order-process" className="px-5 py-2 text-sm font-black text-white rounded bg-[#c81e1e] hover:bg-[#a81818]">Create & Process Part Order</button>}
        </div>
      </div>
    </div>
  );
}

export function PartsOrdersModal({ orders, reload, onNew, onClose }) {
  const setStatus = async (o, status) => { await api("PATCH", `/parts-orders/${o.id}`, { ...o, status, ...(status === "received" && !o.date_received ? { date_received: new Date().toLocaleDateString("en-CA") } : {}) }); reload(); };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="parts-orders-modal">
      <div className="w-full max-w-3xl bg-white rounded-lg shadow-2xl mt-8">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-[#c81e1e] text-white">
          <h3 className="font-black text-sm">Parts Orders</h3>
          <div className="flex items-center gap-3">
            <button onClick={onNew} data-testid="parts-orders-new" className="text-[12px] font-bold bg-white/20 px-2 py-1 rounded inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" />New</button>
            <button onClick={onClose} data-testid="parts-orders-close"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="p-4">
          <div className="border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-[12px] min-w-[560px]">
              <thead className="bg-slate-50 text-slate-600"><tr><th className="px-2 py-1 text-left">W/O</th><th className="px-2 py-1 text-left">Part</th><th className="px-2 py-1 text-left">Supplier</th><th className="px-2 py-1 text-center">Qty</th><th className="px-2 py-1 text-right">Cost</th><th className="px-2 py-1 text-left">Status</th><th className="w-8" /></tr></thead>
              <tbody>
                {orders.length === 0 && <tr><td colSpan={7} className="p-3 text-center text-slate-400">No parts orders.</td></tr>}
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100 align-top" data-testid="parts-order-row">
                    <td className="px-2 py-1 font-mono2">{o.wo_number || "—"}</td>
                    <td className="px-2 py-1"><div className="space-y-0.5">{orderLines(o).map((l, i) => <div key={i}><b className="font-mono2">{Number(l.qty) || 0}×</b> {l.part_name}</div>)}</div></td>
                    <td className="px-2 py-1">{o.supplier}</td>
                    <td className="px-2 py-1 text-center">{orderQty(o)}</td>
                    <td className="px-2 py-1 text-right font-mono2">{money(orderCost(o))}</td>
                    <td className="px-2 py-1">
                      <select value={o.status} onChange={(e) => setStatus(o, e.target.value)} className={cellInp} data-testid="parts-order-status">
                        {PO_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                    <td className="text-center"><button onClick={() => { if (window.confirm("Delete this order?")) api("DELETE", `/parts-orders/${o.id}`).then(reload); }} data-testid="parts-order-del"><Trash2 className="h-3.5 w-3.5 text-slate-300 hover:text-rose-600" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ All Orders — every parts order across all clients (Form_Orders) ============ */
export function AllOrdersModal({ onClose, onNav, brand }) {
  const [orders, setOrders] = useState([]);
  const [names, setNames] = useState({});
  const [view, setView] = useState("outstanding"); // outstanding | received | all
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { setOrders(await api("GET", "/parts-orders")); } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api("GET", "/clients").then((cs) => { const m = {}; (cs || []).forEach((c) => { m[c.id] = c.name; }); setNames(m); }).catch(() => {});
  }, []);
  const OUT = ["to_order", "ordered"];
  const isOut = (o) => OUT.includes(o.status);
  const match = (o) => view === "all" ? o.status !== "cancelled" : view === "received" ? o.status === "received" : isOut(o);
  const shown = orders.filter(match);
  const cnt = (v) => orders.filter((o) => v === "all" ? o.status !== "cancelled" : v === "received" ? o.status === "received" : isOut(o)).length;
  const d = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU") : "");

  const markReceived = async (o) => {
    try { await api("PATCH", `/parts-orders/${o.id}`, { ...o, status: "received", date_received: new Date().toISOString() }); toast.success("Marked received — part added to its work order"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const reopen = async (o) => {
    try { await api("PATCH", `/parts-orders/${o.id}`, { ...o, status: "ordered", date_received: null }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const processOrder = (o) => {
    const ls = orderLines(o).filter((l) => l.part_name);
    if (!ls.length) { toast.error("This order has no parts to process."); return; }
    const subject = `Parts Order ${o.wo_number ? "for W/O " + o.wo_number : ""} from ${brand || "us"}`.trim();
    const partsText = ls.map((l) => `${Number(l.qty) || 0} X ${l.part_name}`).join("\n");
    const body = `Hello ${o.supplier || ""},\n\nWe wish to place an order for the shipment of the following part/s, ASAP.\n\n${partsText}\n\nPlease contact us as a matter of urgency if the part/s are not available or if there will be any delay in delivery.\n\nKindest Regards,\n${brand || ""}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const goToClient = (o) => {
    if (o.client_id && onNav) { onNav(`client:${o.client_id}`); onClose(); }
    else toast.error("No client is linked to this order.");
  };

  const heading = view === "outstanding" ? "OUTSTANDING PARTS ORDERS" : view === "received" ? "RECEIVED PARTS ORDERS" : "ALL PARTS ORDERS";
  const TABS = [["outstanding", "View Outstanding Orders"], ["received", "View Received Orders"], ["all", "View All"]];

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="all-orders-modal">
      <div className="w-full max-w-5xl bg-[#d6dfec] rounded-lg shadow-2xl mt-6 border border-[#8faadc]">
        <div className="flex items-center justify-between px-4 py-2 rounded-t-lg bg-[#8faadc] text-[#1b3a6b]">
          <h3 className="font-black text-sm">{heading}</h3>
          <button onClick={onClose} data-testid="all-orders-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {TABS.map(([v, l]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`all-orders-tab-${v}`}
                className={`px-3.5 py-2 rounded-md text-[13px] font-bold border-2 transition-colors ${view === v ? "bg-[#1b3a6b] text-white border-[#1b3a6b]" : "bg-white text-[#1b3a6b] border-[#8faadc] hover:bg-[#eef3fb]"}`}>
                {l} <span className="opacity-70">({cnt(v)})</span>
              </button>
            ))}
          </div>
          <div className="border border-[#8faadc] rounded bg-white overflow-x-auto max-h-[62vh] overflow-y-auto">
            <table className="w-full text-[12px] min-w-[860px]">
              <thead className="bg-[#eef3fb] text-[#1b3a6b] sticky top-0"><tr>
                <th className="px-2 py-1.5 text-left">Date Ordered</th><th className="px-2 py-1.5 text-left">Client</th>
                <th className="px-2 py-1.5 text-left">W/O</th><th className="px-2 py-1.5 text-left">Ordered From</th>
                <th className="px-2 py-1.5 text-left">Part</th><th className="px-2 py-1.5 text-center">Qty</th>
                <th className="px-2 py-1.5 text-right">Cost</th><th className="px-2 py-1.5 text-left">Date Received</th>
                <th className="px-2 py-1.5 text-center">Actions</th>
              </tr></thead>
              <tbody>
                {loading && <tr><td colSpan={9} className="p-4 text-center text-slate-400">Loading orders…</td></tr>}
                {!loading && shown.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-400">No orders in this view.</td></tr>}
                {shown.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100 hover:bg-[#f4f7fc] align-middle" data-testid="all-order-row">
                    <td className="px-2 py-1 font-mono2 whitespace-nowrap">{d(o.date_ordered || o.created_at)}</td>
                    <td className="px-2 py-1">{names[o.client_id] || "—"}</td>
                    <td className="px-2 py-1 font-mono2">{o.wo_number || "—"}</td>
                    <td className="px-2 py-1">{o.supplier}</td>
                    <td className="px-2 py-1 align-top"><div className="space-y-0.5">{orderLines(o).map((l, i) => <div key={i}><b className="font-mono2">{Number(l.qty) || 0}×</b> {l.part_name}</div>)}</div></td>
                    <td className="px-2 py-1 text-center align-top">{orderQty(o)}</td>
                    <td className="px-2 py-1 text-right font-mono2 align-top">{money(orderCost(o))}</td>
                    <td className="px-2 py-1 font-mono2 whitespace-nowrap">{o.status === "received" ? (d(o.date_received) || "✓") : <span className="text-amber-600 font-bold">outstanding</span>}</td>
                    <td className="px-2 py-1">
                      <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                        <button onClick={() => processOrder(o)} data-testid="all-order-process" title="Process / email this order to the supplier" className="rounded border border-sky-300 bg-sky-50 text-sky-700 font-bold px-2 py-1 hover:bg-sky-100">Process</button>
                        {o.status === "received"
                          ? <button onClick={() => reopen(o)} data-testid="all-order-reopen" title="Re-open as outstanding" className="rounded border border-amber-300 bg-amber-50 text-amber-700 font-bold px-2 py-1 hover:bg-amber-100">Re-open</button>
                          : <button onClick={() => markReceived(o)} data-testid="all-order-received" title="Mark as received" className="rounded border border-emerald-300 bg-emerald-50 text-emerald-700 font-bold px-2 py-1 hover:bg-emerald-100">Received</button>}
                        <button onClick={() => goToClient(o)} data-testid="all-order-goclient" title="Go to this client's file" className="rounded border border-slate-300 bg-white text-slate-700 font-bold px-2 py-1 hover:bg-slate-50">Go To Client</button>
                        <button onClick={() => { if (window.confirm("Delete this order?")) api("DELETE", `/parts-orders/${o.id}`).then(load); }} data-testid="all-order-del" title="Delete"><Trash2 className="h-3.5 w-3.5 text-slate-300 hover:text-rose-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ Supplier address book ============ */
export function SuppliersModal({ suppliers, reload, onClose, isAdmin }) {
  const empty = { name: "", contact: "", phone: "", email: "", account_no: "", website: "", notes: "" };
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filesFor, setFilesFor] = useState(null);
  const [supFiles, setSupFiles] = useState([]);
  const openFiles = async (s) => {
    setFilesFor(s);
    try { setSupFiles(await api("GET", `/suppliers/${s.id}/files`)); } catch { setSupFiles([]); }
  };
  const reloadFiles = () => { if (filesFor) api("GET", `/suppliers/${filesFor.id}/files`).then(setSupFiles).catch(() => {}); };
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name.trim()) { toast.error("Supplier name required."); return; }
    setSaving(true);
    try {
      if (editId) { await api("PATCH", `/suppliers/${editId}`, f); toast.success("Supplier updated"); }
      else { await api("POST", "/suppliers", f); toast.success("Supplier added"); }
      setF(empty); setEditId(null); reload();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const edit = (s) => { setEditId(s.id); setF({ name: s.name, contact: s.contact || "", phone: s.phone || "", email: s.email || "", account_no: s.account_no || "", website: s.website || "", notes: s.notes || "" }); };
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const shown = q ? suppliers.filter((s) => {
    const hay = [s.name, s.suburb, s.contact, s.email, s.phone, s.website,
      ...(s.people || []).flatMap((p) => [p.first_name, p.surname, p.position, ...(p.numbers || []).map((n) => n.value)])].join(" ").toLowerCase();
    return hay.includes(q);
  }) : suppliers;
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="suppliers-modal">
      <div className="w-full max-w-3xl bg-white rounded-lg shadow-2xl mt-8">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-[#123a63] text-white">
          <h3 className="font-black text-sm">Supplier / Business Contacts <span className="font-normal opacity-80">({suppliers.length})</span></h3>
          <button onClick={onClose} data-testid="suppliers-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid sm:grid-cols-3 gap-2">
            <input value={f.name} onChange={upd("name")} placeholder="Supplier name *" className={cellInp} data-testid="supplier-name" />
            <input value={f.contact} onChange={upd("contact")} placeholder="Contact person" className={cellInp} data-testid="supplier-contact" />
            <input value={f.phone} onChange={upd("phone")} placeholder="Phone" className={cellInp} data-testid="supplier-phone" />
            <input value={f.email} onChange={upd("email")} placeholder="Email" className={cellInp} data-testid="supplier-email" />
            <input value={f.account_no} onChange={upd("account_no")} placeholder="Account no." className={cellInp} data-testid="supplier-account" />
            <input value={f.website} onChange={upd("website")} placeholder="Website" className={cellInp} data-testid="supplier-website" />
          </div>
          <input value={f.notes} onChange={upd("notes")} placeholder="Notes" className={cellInp} data-testid="supplier-notes" />
          <div className="flex justify-end gap-2">
            {editId && <button onClick={() => { setF(empty); setEditId(null); }} className="px-3 py-1.5 text-sm text-slate-600 rounded hover:bg-slate-100">Cancel edit</button>}
            <button onClick={save} disabled={saving} data-testid="supplier-save" className="px-5 py-2 text-sm font-black text-white rounded bg-[#123a63] hover:bg-[#1a4f8a]">{editId ? "Save changes" : "Add supplier"}</button>
          </div>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search suppliers, couriers, trades — name, suburb, person, phone…" className={cellInp} data-testid="supplier-search" />
          <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {shown.length === 0 && <p className="p-3 text-slate-400 text-sm">No matching suppliers.</p>}
            {shown.map((s) => (
              <div key={s.id} className="px-3 py-2 text-[12px]" data-testid="supplier-row">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-[#123a63] flex items-center gap-1.5">{s.name}{s.delivery && <span className="text-[9px] font-black text-white bg-[#0a7d38] rounded px-1" data-testid="supplier-delivery-badge">DELIVERY</span>}</div>
                    {(s.address || s.suburb) && <div className="text-slate-500">{[s.address, s.suburb, s.state, s.postcode].filter(Boolean).join(" ")}</div>}
                    {s.website && <a href={s.website.startsWith("http") ? s.website : `https://${s.website}`} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline" data-testid="supplier-website-link">{s.website}</a>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button onClick={() => openFiles(s)} data-testid="supplier-files" className="text-[#0a7d38] font-bold hover:underline">Files</button>
                    <button onClick={() => edit(s)} data-testid="supplier-edit" className="text-sky-700 font-bold hover:underline">Edit</button>
                    {isAdmin && <button onClick={() => { if (window.confirm(`Delete ${s.name}?`)) api("DELETE", `/suppliers/${s.id}`).then(reload); }} data-testid="supplier-del"><Trash2 className="h-3.5 w-3.5 text-slate-300 hover:text-rose-600" /></button>}
                  </div>
                </div>
                {(s.people || []).length > 0 ? (
                  <div className="mt-1 pl-2 border-l-2 border-[#dbe6f5] space-y-0.5">
                    {s.people.map((p, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-x-2" data-testid="supplier-person">
                        <span className="font-semibold">{[p.first_name, p.surname].filter(Boolean).join(" ") || "—"}</span>
                        {p.position && <span className="text-slate-500">· {p.position}</span>}
                        {(p.numbers || []).map((n, j) => (
                          <a key={j} href={n.type === "Email" || n.value.includes("@") ? `mailto:${n.value}` : `tel:${n.value.replace(/\s/g, "")}`}
                            className="text-blue-700 hover:underline font-mono2" data-testid="supplier-person-number">{n.type === "Email" || n.value.includes("@") ? "✉" : n.type.replace(" Phone", "")} {n.value}</a>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-slate-600 mt-0.5">{[s.contact, s.phone, s.email].filter(Boolean).join(" · ")}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      {filesFor && <SupplierFilesModal sid={filesFor.id} files={supFiles} reload={reloadFiles}
        supplierName={filesFor.name} onClose={() => { setFilesFor(null); setSupFiles([]); }} />}
    </div>
  );
}

/* ============ Edit invoice details (note / status / delivery) ============ */
export function InvoiceDetailsModal({ sale, client, reload, onClose }) {
  const [f, setF] = useState({
    status: sale.status || "invoiced", note: sale.note || "",
    delivery_date: sale.delivery_date || "", delivery_instructions: sale.delivery_instructions || "",
  });
  const contacts = (client && client.contacts) || [];
  const nameOf = (c) => `${c.first_name || ""} ${c.surname || ""}`.trim() || c.company || "Contact";
  const emailOf = (c) => { const d = (c.details || []).find((x) => /email/i.test(x.type || "") || /@/.test(x.value || "")); return (d && d.value) || c.email || ""; };
  // which contact (or the client) is this invoice billed to?
  const initBill = () => {
    if (sale.addressed_to) { const i = contacts.findIndex((c) => nameOf(c) === sale.addressed_to); if (i >= 0) return String(i); }
    if (sale.addressed_to === "") return "client"; // explicitly reset before
    if (sale.addressed_to == null) { const b = contacts.findIndex((c) => c.bill_to); if (b >= 0) return String(b); }
    return "client";
  };
  const [bill, setBill] = useState(initBill);
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    let addr;
    if (bill === "client") addr = { addressed_to: "", addressed_relationship: "", addressed_email: "", addressed_company: "", addressed_address: "" };
    else { const c = contacts[Number(bill)]; addr = c ? { addressed_to: nameOf(c), addressed_relationship: c.relationship || "", addressed_email: emailOf(c), addressed_company: c.company || "", addressed_address: c.address_text || "" } : {}; }
    try { await api("PATCH", `/sales/${sale.id}`, { ...f, ...addr }); toast.success("Invoice updated"); reload(); onClose(); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="invoice-details-modal">
      <div className="w-full max-w-md bg-white rounded-lg shadow-2xl mt-12 mb-8">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-[#123a63] text-white">
          <h3 className="font-black text-sm">Edit Invoice To · {sale.sale_number}</h3>
          <button onClick={onClose} data-testid="invoice-details-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-2">
          {contacts.length > 0 && (
            <div className="rounded-lg border-2 border-rose-200 bg-rose-50/50 p-2.5" data-testid="invoice-bill-to">
              <span className="block text-[11px] font-black text-rose-700 uppercase tracking-wide mb-1.5">Who gets this invoice? (bill &amp; email)</span>
              <div className="space-y-1 max-h-52 overflow-y-auto">
                <label className={`flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer text-[12px] ${bill === "client" ? "bg-white ring-2 ring-[#123a63]" : "hover:bg-white/70"}`} data-testid="invoice-bill-client">
                  <input type="radio" name="billto" checked={bill === "client"} onChange={() => setBill("client")} className="mt-0.5 h-4 w-4 accent-[#123a63]" />
                  <span><b className="text-slate-800">Just the client</b> — {client.name}<span className="block text-slate-400">Normal invoice, addressed to the client.</span></span>
                </label>
                {contacts.map((c, i) => (
                  <label key={i} className={`flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer text-[12px] ${bill === String(i) ? "bg-white ring-2 ring-rose-400" : "hover:bg-white/70"}`} data-testid={`invoice-bill-contact-${i}`}>
                    <input type="radio" name="billto" checked={bill === String(i)} onChange={() => setBill(String(i))} className="mt-0.5 h-4 w-4 accent-rose-600" />
                    <span className="min-w-0">
                      <b className="text-slate-800">{nameOf(c)}</b>
                      {c.bill_to && <span className="ml-1 rounded-full bg-rose-100 text-rose-700 px-1.5 py-0.5 text-[9px] font-black">BILL TO</span>}
                      {c.relationship && <span className="text-slate-400"> · {c.relationship}</span>}
                      {c.company && <span className="block text-slate-500">{c.company}</span>}
                      {c.address_text && <span className="block text-slate-400 truncate">{c.address_text}</span>}
                      {emailOf(c) && <span className="block text-blue-700 truncate">{emailOf(c)}</span>}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div><label className="block text-[11px] font-bold text-slate-500">Status</label>
            <select value={f.status} onChange={upd("status")} className={cellInp} data-testid="invoice-details-status">
              {["invoiced", "part_paid", "paid", "completed", "Client Counter Sale", "Sold Waiting Delivery", "cancelled"].map((s) => <option key={s}>{s}</option>)}</select></div>
          <div><label className="block text-[11px] font-bold text-slate-500">Delivery date</label>
            <input type="date" value={f.delivery_date} onChange={upd("delivery_date")} className={cellInp} data-testid="invoice-details-delivery-date" /></div>
          <div><label className="block text-[11px] font-bold text-slate-500">Delivery instructions</label>
            <textarea rows={2} value={f.delivery_instructions} onChange={upd("delivery_instructions")} className={cellInp} data-testid="invoice-details-instructions" /></div>
          <div><label className="block text-[11px] font-bold text-slate-500">Note</label>
            <input value={f.note} onChange={upd("note")} className={cellInp} data-testid="invoice-details-note" /></div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 rounded hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="invoice-details-save" className="px-5 py-2 text-sm font-black text-white rounded bg-[#123a63] hover:bg-[#1a4f8a]">Save</button>
        </div>
      </div>
    </div>
  );
}


/* ============ Payment Arrangement (legacy Form_InvoicePaymentArrangement1) ============ */
export function PaymentArrangementModal({ sale, reload, onClose }) {
  const paid = (sale.payments || []).reduce((a, p) => a + (p.amount || 0), 0);
  const balance = Math.max((sale.total || 0) - paid, 0);
  const [arranged, setArranged] = useState(!!sale.payment_arrangement);
  const [note, setNote] = useState(sale.payment_arrangement_notes || "");
  const [saving, setSaving] = useState(false);
  const save = async (payload, msg) => {
    setSaving(true);
    try { await api("PATCH", `/sales/${sale.id}`, payload); toast.success(msg); reload(); onClose(); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="payment-arrangement-modal">
      <div className="w-full max-w-md bg-white rounded-lg shadow-2xl mt-16">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-[#123a63] text-white">
          <h3 className="font-black text-sm">Invoice Payment Arrangement · {sale.sale_number}</h3>
          <button onClick={onClose} data-testid="payment-arrangement-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3 text-[13px]">
          <div className="flex justify-between"><span className="text-slate-500">Invoice To</span><b className="uppercase">{sale.invoice_to || sale.addressed_to || sale.client_name || ""}</b></div>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="bg-slate-50 rounded p-2"><div className="text-slate-500 text-[11px]">Nett</div><div className="font-black">{money(sale.total)}</div></div>
            <div className="bg-slate-50 rounded p-2"><div className="text-slate-500 text-[11px]">Balance Due</div><div className="font-black text-rose-600">{money(balance)}</div></div>
          </div>
          <label className="flex items-center gap-2 font-bold text-[#123a63] cursor-pointer">
            <input type="checkbox" checked={arranged} onChange={(e) => setArranged(e.target.checked)} data-testid="payment-arrangement-confirm" />
            Confirm Payment Arrangement
          </label>
          <div><label className="block text-[11px] font-bold text-slate-500">Arrangement notes</label>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className={cellInp} data-testid="payment-arrangement-note" placeholder="e.g. $50/week by direct debit from 1st" /></div>
        </div>
        <div className="flex justify-between gap-2 px-4 py-3 border-t bg-slate-50">
          <button onClick={() => save({ payment_arrangement: false, payment_arrangement_notes: "" }, "Arrangement removed")} disabled={saving} data-testid="payment-arrangement-remove" className="px-3 py-2 text-sm font-semibold text-rose-600 rounded hover:bg-rose-50">Remove Arrangement</button>
          <button onClick={() => save({ payment_arrangement: arranged, payment_arrangement_notes: note }, "Arrangement saved")} disabled={saving} data-testid="payment-arrangement-save" className="px-5 py-2 text-sm font-black text-white rounded bg-[#123a63] hover:bg-[#1a4f8a]">SAVE and EXIT</button>
        </div>
      </div>
    </div>
  );
}

/* ============ Delivery By / Delivery Method (legacy Form_DeliveryMethodSelection) ============ */
export function DeliveryByModal({ sale, suppliers = [], reload, onClose }) {
  const [method, setMethod] = useState(sale.delivery_method || "");
  const [instructions, setInstructions] = useState(sale.delivery_instructions || "");
  const [saving, setSaving] = useState(false);
  const COMMON = ["To Be Delivered", "Customer Pickup", "Company Delivery", "Courier", "Freight"];
  const supplierNames = (suppliers || []).map((s) => s.name).filter(Boolean).sort((a, b) => a.localeCompare(b));
  const save = async () => {
    setSaving(true);
    try { await api("PATCH", `/sales/${sale.id}`, { delivery_method: method, delivery_instructions: instructions }); toast.success("Delivery method saved"); reload(); onClose(); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="delivery-by-modal">
      <div className="w-full max-w-md bg-white rounded-lg shadow-2xl mt-16">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-[#123a63] text-white">
          <h3 className="font-black text-sm">Delivery Method Selector · {sale.sale_number}</h3>
          <button onClick={onClose} data-testid="delivery-by-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3 text-[13px]">
          <div><label className="block text-[11px] font-bold text-slate-500">Delivery By <span className="font-normal text-slate-400">(Business Contacts · {supplierNames.length})</span></label>
            <input list="delivery-methods" value={method} onChange={(e) => setMethod(e.target.value)} className={cellInp} data-testid="delivery-by-method" placeholder="Choose a courier/supplier or type a method" />
            <datalist id="delivery-methods">
              {COMMON.map((m) => <option key={m} value={m} />)}
              {supplierNames.map((n) => <option key={n} value={n} />)}
            </datalist>
          </div>
          <div><label className="block text-[11px] font-bold text-slate-500">Delivery Instructions</label>
            <textarea rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} className={cellInp} data-testid="delivery-by-instructions" /></div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 rounded hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="delivery-by-save" className="px-5 py-2 text-sm font-black text-white rounded bg-[#123a63] hover:bg-[#1a4f8a]">Save and Close</button>
        </div>
      </div>
    </div>
  );
}


/* ============ Stock Allocator (legacy Form_Stock · StockSpa) ============ */
export function StockAllocatorModal({ sale, reload, onClose }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const load = React.useCallback(async (query) => {
    setLoading(true);
    try { setRows(await api("GET", `/stock/spas${query ? `?q=${encodeURIComponent(query)}` : ""}`)); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q, load]);
  const allocate = async (p) => {
    setBusy(p.id);
    try { await api("POST", `/sales/${sale.id}/allocate-spa`, { product_id: p.id }); toast.success(`Allocated ${p.name} · J# ${p.sku || p.id.slice(0, 8)}`); reload(); onClose(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="stock-allocator-modal">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-2xl mt-12">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-[#123a63] text-white">
          <h3 className="font-black text-sm">Stock Allocator — allocate a Spa to Invoice {sale.sale_number}</h3>
          <button onClick={onClose} data-testid="stock-allocator-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search spa stock by name or SKU…" className={cellInp} data-testid="stock-allocator-search" autoFocus />
          {sale.jnumber && <div className="mt-2 text-[12px] text-emerald-700 font-bold" data-testid="stock-allocator-current">Currently allocated J#: {sale.jnumber}</div>}
          <div className="mt-2 border border-slate-200 rounded divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {loading && <p className="p-3 text-slate-400 text-sm">Loading spa stock…</p>}
            {!loading && rows.length === 0 && <p className="p-3 text-slate-400 text-sm">No spa stock matches.</p>}
            {!loading && rows.map((p) => {
              const out = (p.qty || 0) <= 0;
              return (
                <div key={p.id} className="flex items-center gap-2 px-3 py-1.5 text-[12px]" data-testid="stock-allocator-row">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#123a63] truncate">{p.name}</div>
                    <div className="text-slate-500">{p.sku || "—"}{p.brand ? ` · ${p.brand}` : ""}{p.location ? ` · ${p.location}` : ""}</div>
                  </div>
                  <div className={`w-16 text-center font-black ${out ? "text-rose-600" : "text-emerald-700"}`}>{out ? "OUT" : `${p.qty} in`}</div>
                  <div className="w-20 text-right font-mono2">{money(p.price)}</div>
                  <button onClick={() => allocate(p)} disabled={out || busy === p.id} data-testid="stock-allocator-allocate" className="px-3 py-1 rounded bg-[#123a63] text-white font-bold text-[11px] hover:bg-[#1a4f8a] disabled:opacity-40">{busy === p.id ? "…" : "Allocate"}</button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
